import type {AnchorHTMLAttributes} from 'react';

import styled from '@emotion/styled';
import Link, {type LinkProps} from 'next/link';

import {transient} from 'Application/configs/emotionTransientProps';

import type {WithChildren} from 'types/global';

type LinkPropType = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> & LinkProps;

/**
 * The props for the `LinkBasic` component, which renders a styled link wrapper
 * with optional alignment and testing support.
 * This interface defines the alignment options and testing identifier used
 * to configure the component.
 */
interface ComponentProps {
    /**
     * Determines the alignment of the link within its container.
     * Can be set to `'center'`, `'left'`, or `'right'`.
     */
    $align?: 'center' | 'left' | 'right';
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'LinkBasic'
     */
    testId?: string;
}

/**
 * A basic link component that wraps its children with styled link behavior.
 * The `LinkBasic` component is a styled wrapper around a standard link,
 * extended with alignment options and a `testId` for testing. It supports
 * spreading additional link-related props via `linkProps` to allow flexible usage.
 * The component applies a `data-cy` attribute for end-to-end testing
 * and ensures consistent alignment styling using the `$align` prop.
 *
 * @param props           The component props.
 * @param props.$align    Optional alignment setting for the link’s wrapper (e.g., left, center, right).
 * @param props.children  The content to be wrapped by the link, typically text or inline elements.
 * @param props.testId    An optional identifier used for testing, applied as `data-cy`. Defaults to `'LinkBasic'`.
 * @param props.linkProps Additional link-related props that are spread onto the `Wrapper`.
 * @returns A JSX element rendering a styled link wrapper around its children.
 *
 * @example
 * ```tsx
 * <LinkBasic href="/about" $align="center">
 *   Learn more
 * </LinkBasic>
 * ```
 */
const LinkBasic = ({
    $align,
    children,
    testId = 'LinkBasic',
    ...linkProps
}: WithChildren<ComponentProps> & LinkPropType) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Wrapper $align={$align} data-cy={testId} {...linkProps}>{children}</Wrapper>
);

LinkBasic.displayName = 'LinkBasic';

export {LinkBasic};

interface WrapperProps {
    $align?: 'center' | 'left' | 'right';
}

const Wrapper = styled(Link, transient)<WrapperProps>`
    text-align: ${({$align}) => $align ?? 'left'};
    transition: outline 0.2s ease-in-out;

    &:focus-visible {
        outline-color: ${({theme}) => theme.colors.focus};
        outline-offset: 4px;
        outline-style: solid;
        outline-width: 4px;
        transition: outline 0.2s ease-in-out;
    }
`;